import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Sparkles, Cloud, Server, Database, Cpu, Shield, Zap, BookOpen, Code, Brain, Globe, GraduationCap, Award, Network, Laptop, Terminal, Layers } from 'lucide-react';

const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{
      background: 'linear-gradient(135deg, #0B0F1A 0%, #020617 100%)'
    }}>
      {/* Animated grid background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(#00E5FF 1px, transparent 1px), linear-gradient(90deg, #00E5FF 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Glow orbs */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-[#00E5FF] rounded-full blur-[150px] opacity-20 animate-pulse"></div>
      <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#1DE9B6] rounded-full blur-[150px] opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid xl:grid-cols-2 gap-12 items-center">
          {/* Left Side - Text Content */}
          <div className="space-y-8 animate-fade-in">
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/10 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-[#00E5FF]" />
              <span className="text-sm font-medium text-[#00E5FF]">Future-Ready Solutions</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl xl:text-6xl font-bold leading-tight font-['Space_Grotesk']">
              <span className="text-white">Learn Cloud.</span>
              <br />
              <span className="text-white">Think AI.</span>
              <br />
              <span className="relative">
                <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">
                  Build the Future.
                </span>
                <div className="absolute -bottom-2 left-0 w-48 h-1 bg-gradient-to-r from-[#00E5FF] to-transparent"></div>
              </span>
            </h1>

            <p className="text-base sm:text-xl text-[#38BDF8] font-medium">
              Corporate Training | IT Services | Recruitment | Software Solutions
            </p>

            <p className="text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
              Empowering individuals and enterprises with future-ready skills and scalable digital solutions.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollToSection('footer')}
                className="bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold px-8 py-6 text-lg rounded-lg shadow-lg shadow-[#00E5FF]/40 hover:shadow-[#00E5FF]/60 transition-all duration-300 group"
              >
                Get in Touch
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => scrollToSection('services')}
                className="border-2 border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 px-8 py-6 text-lg rounded-lg transition-all duration-300 bg-transparent"
              >
                Explore Services
              </Button>
            </div>
          </div>

          {/* Right Side - Animated Cloud Visual with 3D Icons */}
          <div className="relative h-[500px] hidden xl:block">
            {/* Main Cloud Container */}
            <div className="absolute inset-0 flex items-center justify-center">
              {/* Central Cloud Icon */}
              <div className="relative">
                {/* Pulsing rings */}
                <div className="absolute inset-0 -m-10 border-2 border-[#00E5FF]/20 rounded-full animate-ping" style={{ animationDuration: '3s' }}></div>
                <div className="absolute inset-0 -m-20 border border-[#00E5FF]/10 rounded-full animate-ping" style={{ animationDuration: '4s', animationDelay: '0.5s' }}></div>
                <div className="absolute inset-0 -m-32 border border-[#00E5FF]/5 rounded-full animate-ping" style={{ animationDuration: '5s', animationDelay: '1s' }}></div>
                
                {/* Main Cloud - 3D Effect */}
                <div className="relative w-36 h-36 rounded-full flex items-center justify-center animate-bounce" 
                  style={{ 
                    animationDuration: '3s',
                    background: 'linear-gradient(145deg, #00E5FF, #0891b2)',
                    boxShadow: '0 20px 60px rgba(0, 229, 255, 0.5), inset 0 -5px 20px rgba(0, 0, 0, 0.2), inset 0 5px 20px rgba(255, 255, 255, 0.2)',
                    transform: 'perspective(500px) rotateX(10deg)'
                  }}>
                  <Cloud className="w-16 h-16 text-[#0B0F1A] drop-shadow-lg" />
                </div>
              </div>
            </div>

            {/* 3D Floating Technology Icons - Outer Ring */}
            
            {/* Server - Top */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 animate-float" style={{ animationDelay: '0s' }}>
              <div className="icon-3d bg-gradient-to-br from-[#111827] to-[#1f2937]" style={{ transform: 'perspective(500px) rotateX(-15deg) rotateY(10deg)' }}>
                <Server className="w-7 h-7 text-[#00E5FF]" />
              </div>
            </div>

            {/* Database - Top Right */}
            <div className="absolute top-16 right-4 animate-float" style={{ animationDelay: '0.3s' }}>
              <div className="icon-3d bg-gradient-to-br from-[#111827] to-[#1f2937]" style={{ transform: 'perspective(500px) rotateX(-10deg) rotateY(-15deg)' }}>
                <Database className="w-7 h-7 text-[#1DE9B6]" />
              </div>
            </div>

            {/* Code - Right */}
            <div className="absolute top-1/3 right-0 animate-float" style={{ animationDelay: '0.6s' }}>
              <div className="icon-3d bg-gradient-to-br from-[#111827] to-[#1f2937]" style={{ transform: 'perspective(500px) rotateY(-20deg)' }}>
                <Code className="w-7 h-7 text-[#38BDF8]" />
              </div>
            </div>

            {/* Brain/AI - Bottom Right */}
            <div className="absolute bottom-1/3 right-2 animate-float" style={{ animationDelay: '0.9s' }}>
              <div className="icon-3d bg-gradient-to-br from-[#111827] to-[#1f2937]" style={{ transform: 'perspective(500px) rotateX(10deg) rotateY(-15deg)' }}>
                <Brain className="w-7 h-7 text-[#a78bfa]" />
              </div>
            </div>

            {/* Shield - Bottom */}
            <div className="absolute bottom-8 right-1/4 animate-float" style={{ animationDelay: '1.2s' }}>
              <div className="icon-3d bg-gradient-to-br from-[#111827] to-[#1f2937]" style={{ transform: 'perspective(500px) rotateX(15deg)' }}>
                <Shield className="w-7 h-7 text-[#f472b6]" />
              </div>
            </div>

            {/* CPU - Bottom Left */}
            <div className="absolute bottom-8 left-1/4 animate-float" style={{ animationDelay: '1.5s' }}>
              <div className="icon-3d bg-gradient-to-br from-[#111827] to-[#1f2937]" style={{ transform: 'perspective(500px) rotateX(15deg) rotateY(10deg)' }}>
                <Cpu className="w-7 h-7 text-[#fbbf24]" />
              </div>
            </div>

            {/* Globe/Network - Left */}
            <div className="absolute bottom-1/3 left-2 animate-float" style={{ animationDelay: '1.8s' }}>
              <div className="icon-3d bg-gradient-to-br from-[#111827] to-[#1f2937]" style={{ transform: 'perspective(500px) rotateY(20deg)' }}>
                <Globe className="w-7 h-7 text-[#34d399]" />
              </div>
            </div>

            {/* Layers - Top Left */}
            <div className="absolute top-1/3 left-0 animate-float" style={{ animationDelay: '2.1s' }}>
              <div className="icon-3d bg-gradient-to-br from-[#111827] to-[#1f2937]" style={{ transform: 'perspective(500px) rotateX(-10deg) rotateY(15deg)' }}>
                <Layers className="w-7 h-7 text-[#00E5FF]" />
              </div>
            </div>

            {/* BookOpen - Top Left Corner */}
            <div className="absolute top-16 left-4 animate-float" style={{ animationDelay: '2.4s' }}>
              <div className="icon-3d bg-gradient-to-br from-[#111827] to-[#1f2937]" style={{ transform: 'perspective(500px) rotateX(-10deg) rotateY(15deg)' }}>
                <BookOpen className="w-7 h-7 text-[#fb923c]" />
              </div>
            </div>

            {/* Inner Ring - Course/Education Icons */}
            
            {/* GraduationCap - Inner Top */}
            <div className="absolute top-24 left-1/2 -translate-x-1/2 ml-8 animate-float" style={{ animationDelay: '0.2s' }}>
              <div className="icon-3d-small bg-gradient-to-br from-[#0f172a] to-[#1e293b]" style={{ transform: 'perspective(400px) rotateX(-10deg)' }}>
                <GraduationCap className="w-5 h-5 text-[#00E5FF]" />
              </div>
            </div>

            {/* Award/Certificate - Inner Right */}
            <div className="absolute top-1/2 right-16 -translate-y-1/2 animate-float" style={{ animationDelay: '0.5s' }}>
              <div className="icon-3d-small bg-gradient-to-br from-[#0f172a] to-[#1e293b]" style={{ transform: 'perspective(400px) rotateY(-15deg)' }}>
                <Award className="w-5 h-5 text-[#fbbf24]" />
              </div>
            </div>

            {/* Terminal - Inner Bottom */}
            <div className="absolute bottom-24 left-1/2 -translate-x-1/2 -ml-8 animate-float" style={{ animationDelay: '0.8s' }}>
              <div className="icon-3d-small bg-gradient-to-br from-[#0f172a] to-[#1e293b]" style={{ transform: 'perspective(400px) rotateX(10deg)' }}>
                <Terminal className="w-5 h-5 text-[#1DE9B6]" />
              </div>
            </div>

            {/* Laptop - Inner Left */}
            <div className="absolute top-1/2 left-16 -translate-y-1/2 animate-float" style={{ animationDelay: '1.1s' }}>
              <div className="icon-3d-small bg-gradient-to-br from-[#0f172a] to-[#1e293b]" style={{ transform: 'perspective(400px) rotateY(15deg)' }}>
                <Laptop className="w-5 h-5 text-[#38BDF8]" />
              </div>
            </div>

            {/* Zap - Inner Diagonal */}
            <div className="absolute top-32 right-24 animate-float" style={{ animationDelay: '1.4s' }}>
              <div className="icon-3d-small bg-gradient-to-br from-[#0f172a] to-[#1e293b]" style={{ transform: 'perspective(400px) rotateX(-5deg) rotateY(-10deg)' }}>
                <Zap className="w-5 h-5 text-[#f472b6]" />
              </div>
            </div>

            {/* Network - Inner Diagonal */}
            <div className="absolute bottom-32 left-24 animate-float" style={{ animationDelay: '1.7s' }}>
              <div className="icon-3d-small bg-gradient-to-br from-[#0f172a] to-[#1e293b]" style={{ transform: 'perspective(400px) rotateX(5deg) rotateY(10deg)' }}>
                <Network className="w-5 h-5 text-[#a78bfa]" />
              </div>
            </div>

            {/* Floating particles */}
            <div className="absolute top-1/4 right-1/4 w-2 h-2 bg-[#00E5FF] rounded-full animate-pulse opacity-60"></div>
            <div className="absolute bottom-1/3 left-1/3 w-3 h-3 bg-[#1DE9B6] rounded-full animate-pulse opacity-60" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 right-1/3 w-2 h-2 bg-[#38BDF8] rounded-full animate-pulse opacity-60" style={{ animationDelay: '0.5s' }}></div>
            <div className="absolute bottom-1/4 right-1/4 w-2 h-2 bg-[#00E5FF] rounded-full animate-pulse opacity-60" style={{ animationDelay: '1.5s' }}></div>
            <div className="absolute top-1/3 left-1/4 w-2 h-2 bg-[#fbbf24] rounded-full animate-pulse opacity-60" style={{ animationDelay: '2s' }}></div>
            <div className="absolute bottom-1/2 right-1/5 w-1.5 h-1.5 bg-[#a78bfa] rounded-full animate-pulse opacity-60" style={{ animationDelay: '0.7s' }}></div>
            
            {/* Connecting lines effect */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20">
              <defs>
                <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#00E5FF" stopOpacity="0" />
                  <stop offset="50%" stopColor="#00E5FF" stopOpacity="1" />
                  <stop offset="100%" stopColor="#00E5FF" stopOpacity="0" />
                </linearGradient>
              </defs>
              <line x1="50%" y1="20%" x2="50%" y2="35%" stroke="url(#lineGrad)" strokeWidth="1" strokeDasharray="4,4" className="animate-dash" />
              <line x1="75%" y1="35%" x2="65%" y2="45%" stroke="url(#lineGrad)" strokeWidth="1" strokeDasharray="4,4" className="animate-dash" />
              <line x1="25%" y1="35%" x2="35%" y2="45%" stroke="url(#lineGrad)" strokeWidth="1" strokeDasharray="4,4" className="animate-dash" />
              <line x1="50%" y1="65%" x2="50%" y2="80%" stroke="url(#lineGrad)" strokeWidth="1" strokeDasharray="4,4" className="animate-dash" />
            </svg>

            {/* Neon glow effects */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-[#00E5FF] rounded-full blur-[120px] opacity-15 animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Custom styles for 3D icons */}
      <style jsx>{`
        .icon-3d {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid rgba(0, 229, 255, 0.3);
          box-shadow: 
            0 10px 30px rgba(0, 0, 0, 0.4),
            0 5px 15px rgba(0, 229, 255, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.1),
            inset 0 -1px 0 rgba(0, 0, 0, 0.2);
          transition: all 0.3s ease;
        }
        .icon-3d:hover {
          transform: perspective(500px) rotateX(0deg) rotateY(0deg) scale(1.15) !important;
          box-shadow: 
            0 15px 40px rgba(0, 0, 0, 0.5),
            0 8px 20px rgba(0, 229, 255, 0.4),
            inset 0 1px 0 rgba(255, 255, 255, 0.15);
        }
        .icon-3d-small {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(0, 229, 255, 0.2);
          box-shadow: 
            0 8px 24px rgba(0, 0, 0, 0.3),
            0 4px 12px rgba(0, 229, 255, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.08);
          transition: all 0.3s ease;
        }
        .icon-3d-small:hover {
          transform: perspective(400px) rotateX(0deg) rotateY(0deg) scale(1.2) !important;
          box-shadow: 
            0 12px 32px rgba(0, 0, 0, 0.4),
            0 6px 16px rgba(0, 229, 255, 0.3);
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
